/**
 * DashboardLayout Component
 *
 * Modern layout with glassmorphism over a configurable dashboard background
 * (issue #414). The background changes automatically depending on the
 * active sidebar section (Operaciones / Comercial / Almacén /
 * Administración — see utils/backgroundSection.js), unless the user pinned
 * a different mode ("Animado", "Sólido", or a fixed image) from the
 * Settings > Apariencia selector on the Profile page.
 *
 * @module components/layout/DashboardLayout
 */

import React, { Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import './DashboardLayout.css';
import {
  useBackgroundPreference,
  BACKGROUND_MODES,
  BACKGROUND_MODE_OPTIONS,
  SECTION_IMAGE_OPTIONS,
} from '../../hooks/useBackgroundPreference';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { BackgroundPreferenceContext } from '../../contexts/BackgroundPreferenceContext';
import { getBackgroundForPath, DEFAULT_SECTION_BACKGROUND } from '../../utils/backgroundSection';

// Code-split: remotion + @remotion/player only get downloaded when a user
// actually picks "Animado" — every other page (the overwhelming majority)
// never pays for this bundle. Keeps the ~30 pages that render
// <DashboardLayout> from shipping the Player to everyone by default (#414).
const AnimatedBackground = lazy(() => import('../dashboard/AnimatedBackground'));

const SECTION_IMAGE_SOURCES = {
  cleanroom: {
    desktop: `${process.env.PUBLIC_URL}/images/backgrounds/bg-cleanroom.jpg`,
    mobile: `${process.env.PUBLIC_URL}/images/backgrounds/bg-cleanroom-mobile.jpg`,
  },
  comercial: {
    desktop: `${process.env.PUBLIC_URL}/images/backgrounds/bg-comercial.jpg`,
    mobile: `${process.env.PUBLIC_URL}/images/backgrounds/bg-comercial-mobile.jpg`,
  },
  almacen: {
    desktop: `${process.env.PUBLIC_URL}/images/backgrounds/bg-almacen.jpg`,
    mobile: `${process.env.PUBLIC_URL}/images/backgrounds/bg-almacen-mobile.jpg`,
  },
  technical: {
    desktop: `${process.env.PUBLIC_URL}/images/backgrounds/bg-technical.jpg`,
    mobile: `${process.env.PUBLIC_URL}/images/backgrounds/bg-technical-mobile.jpg`,
  },
};

const isSectionImageId = (id) => Object.prototype.hasOwnProperty.call(SECTION_IMAGE_SOURCES, id);

/**
 * Resolve what to actually render behind the dashboard: one of the four
 * section image ids, 'solid', or 'animated'.
 */
const resolveBackground = (preference, pathname) => {
  switch (preference.mode) {
    case BACKGROUND_MODES.ANIMATED:
      return 'animated';
    case BACKGROUND_MODES.SOLID:
      return 'solid';
    case BACKGROUND_MODES.IMAGE:
      return isSectionImageId(preference.fixedImageId) ? preference.fixedImageId : DEFAULT_SECTION_BACKGROUND;
    case BACKGROUND_MODES.AUTO:
    default:
      return getBackgroundForPath(pathname);
  }
};

const DashboardLayout = ({ children, className = '', variant = 'default' }) => {
  const location = useLocation();
  const { preference, setMode, setFixedImageId } = useBackgroundPreference();
  const isSmallScreen = useMediaQuery('(max-width: 767px)');

  // #569 — la variante "superficie de trabajo": lienzo plano, tinta oscura por
  // defecto. El nodo de fondo NO se monta (no basta con ocultarlo por CSS: un
  // `display:none` sobre un div con `background-image` no garantiza que el
  // navegador se ahorre la descarga, y "en móvil la foto es descarga y batería"
  // es una de las razones honestas por las que se saca de las pantallas
  // operativas). Ver el bloque `--work` en DashboardLayout.css.
  const isWork = variant === 'work';

  const resolvedBackground = resolveBackground(preference, location.pathname);
  const isSectionImage = isSectionImageId(resolvedBackground);
  const imageSrc = isSectionImage
    ? (isSmallScreen ? SECTION_IMAGE_SOURCES[resolvedBackground].mobile : SECTION_IMAGE_SOURCES[resolvedBackground].desktop)
    : null;

  const contextValue = React.useMemo(
    () => ({
      preference,
      resolvedBackground,
      setMode,
      setFixedImageId,
      modeOptions: BACKGROUND_MODE_OPTIONS,
      imageOptions: SECTION_IMAGE_OPTIONS,
    }),
    [preference, resolvedBackground, setMode, setFixedImageId]
  );

  return (
    <div className={`dashboard-layout${isWork ? ' dashboard-layout--work' : ''}`}>
      {/* Fixed Background — variant driven by section/mode preference.
          Keyed on the resolved id so React remounts the node on change,
          which restarts the CSS fade-in below (no cross-page flash). */}
      {!isWork && (
        <div
          key={resolvedBackground}
          className={`dashboard-background dashboard-background--${resolvedBackground} dashboard-background-fade-in`}
          style={imageSrc ? { backgroundImage: `url(${imageSrc})` } : undefined}
        >
          {resolvedBackground === 'animated' && (
            <Suspense fallback={<div className="animated-background-static" aria-hidden="true" />}>
              <AnimatedBackground />
            </Suspense>
          )}
        </div>
      )}

      <BackgroundPreferenceContext.Provider value={contextValue}>
        {/* Glassmorphism Content Wrapper */}
        <div className={`dashboard-content-wrapper ${className}`}>
          {children}
        </div>
      </BackgroundPreferenceContext.Provider>
    </div>
  );
};

export default DashboardLayout;
